import { Button, Dialog, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Card } from '$components/card';
import { SubscriptionDeviceUsage } from '$components/subscription-dialog/subscription-device-usage/subscription-device-usage';
import { SubscriptionNeedMoreDevices } from '$components/subscription-dialog/subscription-device-usage/subscription-need-more-devices';

export const subscriptionNavigationPath =
  'https://www.eppendorf.link/visionize-subscription';
export const helpEmail = 'visionize.support@eppendorf.com';

export interface SubscriptionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organizationName: string;
  subscription?: string;
  subscriptionType?: string;
  licensedDevices?: number;
  usedDevices?: number;
}

function SubscriptionInfoBullets({ bullets }: { bullets: string[] }): ReactElement {
  const bulletsWithSpacers = bullets.flatMap((bullet, index) =>
    index > 0 ? [null, bullet] : [bullet],
  );
  const renderBullet = (bullet: string | null, index: number) =>
    bullet == null ? (
      <p className="font-size-xs" key={`bullet-${index}`}>
        &bull;
      </p>
    ) : (
      <p className="font-size-m p-bottom-xxs" key={`bullet-text-${bullet}`}>
        {bullet}
      </p>
    );
  const elements = bulletsWithSpacers.map(renderBullet);
  return (
    <div className="flex flex__dir--row gap-xs flex-wrap flex__ai--center flex__jc--center gap-xs p-y-xs p-x-l">
      {elements}
    </div>
  );
}

export function SubscriptionDialog({
  isOpen,
  onOpenChange,
  organizationName,
  subscription,
  subscriptionType,
  licensedDevices,
  usedDevices,
}: SubscriptionDialogProps): ReactElement {
  const { t } = useTranslation();
  const essentialBullets = [
    t('subscription.features.realTimeMonitoring'),
    t('subscription.features.emailNotifications'),
    t('subscription.features.dataAccess', { days: 7 }),
    t('subscription.features.deviceDataExport'),
  ];
  const sampleSafetyPlusBullets = [
    t('subscription.features.realTimeMonitoring'),
    t('subscription.features.emailNotifications'),
    t('subscription.features.dataAccess', { days: 90 }),
    t('subscription.features.deviceDataExport'),
    t('subscription.features.alertEscalation'),
    t('subscription.features.auditTrail'),
  ];
  const isEssentials = subscription === 'Essentials';
  const bullets = isEssentials ? essentialBullets : sampleSafetyPlusBullets;

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} className="w-max-4xl">
      <div className="flex flex__ai--center m-bottom-xxl">
        <Icon
          name="organization"
          size={IconSizeClasses.Small}
          className="m-right-m m-bottom-xxs"
        />
        <Dialog.Title className="m-bottom-xxs">{organizationName}</Dialog.Title>
      </div>
      <Dialog.Description />
      <Card>
        <Card.Body>
          <div className="flex flex__dir--column gap-l">
            {subscription && (
              <h2 className="p-y-xs text-align-center font-size-xl font-weight-semibold">
                {subscription} {subscriptionType}
              </h2>
            )}
            <SubscriptionInfoBullets bullets={bullets} />
            <SubscriptionDeviceUsage
              licensedDevices={licensedDevices}
              usedDevices={usedDevices}
            />
            <SubscriptionNeedMoreDevices />
          </div>
        </Card.Body>
      </Card>

      <Dialog.Actions>
        <Dialog.Close>
          <Button variant="secondary">{t('shared.close')}</Button>
        </Dialog.Close>
        <Button variant="primary">
          <Link
            to={subscriptionNavigationPath}
            target="_blank"
            className="flex flex__ai--center"
          >
            {t('subscription.manageSubscription')}
            <Icon
              name="external-link"
              size={IconSizeClasses.XXSmall}
              className="m-left-s bg-white"
            />
          </Link>
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
